'use client';

import { useRef } from 'react';
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  useMotionValue,
  useAnimationFrame,
} from 'motion/react';
import { wrap } from 'motion';
import { useReducedMotion } from '@/lib/use-reduced-motion';
import { useAutoplayInView } from '@/hooks/use-autoplay-in-view';
import { cn } from '@/lib/utils';

interface KeywordRowProps {
  children: React.ReactNode;
  baseVelocity?: number;
  maxScrollFactor?: number;
  className?: string;
}

export default function KeywordRow({
  children,
  baseVelocity = 18,
  maxScrollFactor = 0.6,
  className,
}: KeywordRowProps) {
  const prefersReducedMotion = useReducedMotion();
  const { ref: containerRef, isInView } = useAutoplayInView();
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);

  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 60,
    stiffness: 320,
  });

  const velocityFactor = useTransform(
    smoothVelocity,
    [-1500, 0, 1500],
    [-maxScrollFactor, 0, maxScrollFactor],
    { clamp: true },
  );

  const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);
  const directionFactor = useRef<number>(1);

  useAnimationFrame((_t, delta) => {
    if (prefersReducedMotion || !isInView) return;
    let moveBy = directionFactor.current * baseVelocity * (delta / 1000);
    if (velocityFactor.get() < 0) {
      directionFactor.current = -1;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 1;
    }
    moveBy += directionFactor.current * moveBy * velocityFactor.get();
    baseX.set(baseX.get() + moveBy);
  });

  if (prefersReducedMotion) {
    return (
      <div
        ref={containerRef}
        className={cn('overflow-hidden whitespace-nowrap text-center', className)}
      >
        <span className="block px-4">{children}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="overflow-hidden whitespace-nowrap">
      <motion.div style={{ x }} className={cn('inline-flex whitespace-nowrap', className)}>
        <span className="block">{children}</span>
        <span className="block">{children}</span>
        <span className="block">{children}</span>
        <span className="block">{children}</span>
      </motion.div>
    </div>
  );
}
